/**
 * Universal OpenAPI Schema to Form Parser
 * Works with any OpenAPI specification - certificates, mTLS, or any other API
 */

export interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'file' | 'hidden' | 'datetime' | 'email' | 'tel';
  required?: boolean;
  defaultValue?: any;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
  rows?: number; // for textarea
  acceptedTypes?: string[]; // for file
  maxSize?: number; // for file
  pattern?: string; // for validation
  minLength?: number; // for string validation
  maxLength?: number; // for string validation
  min?: number; // for number validation
  max?: number; // for number validation
  multiple?: boolean; // for multi-select
}

interface OpenAPIProperty {
  type: string;
  description?: string;
  example?: string;
  enum?: string[];
  format?: string;
  pattern?: string;
  items?: {
    type: string;
    enum?: string[];
  };
  maxLength?: number;
  minLength?: number;
  minimum?: number;
  maximum?: number;
}

interface OpenAPISchema {
  type: string;
  required?: string[];
  properties: Record<string, OpenAPIProperty>;
}

/**
 * Maps OpenAPI property types to form field types
 */
function mapOpenAPITypeToFieldType(property: OpenAPIProperty, fieldName: string): FieldConfig['type'] {
  // Handle arrays (like subjectAlternativeNames, keyUsage)
  if (property.type === 'array') {
    if (property.items?.enum) {
      return 'select'; // Multi-select for enum arrays
    }
    return 'textarea'; // Text area for string arrays
  }

  // Handle enums as select dropdowns
  if (property.enum) {
    return 'select';
  }

  // Handle specific formats
  if (property.format) {
    switch (property.format) {
      case 'email':
        return 'email';
      case 'date-time':
      case 'date':
        return 'datetime';
      case 'tel':
      case 'phone':
        return 'tel';
      default:
        break;
    }
  }

  // Handle string types
  if (property.type === 'string') {
    // Smart detection of date fields even when not properly formatted
    if (fieldName.toLowerCase().includes('date') || 
        fieldName.toLowerCase().includes('expiry') ||
        fieldName.toLowerCase().includes('expires') ||
        fieldName.toLowerCase().includes('issued') ||
        fieldName.toLowerCase().includes('created') ||
        fieldName.toLowerCase().includes('updated')) {
      return 'datetime';
    }

    // Long text fields as textarea
    if (fieldName.toLowerCase().includes('description') ||
        fieldName.toLowerCase().includes('notes') ||
        fieldName.toLowerCase().includes('comment') ||
        fieldName.toLowerCase().includes('alternative') || // for subjectAlternativeNames
        (property.description && property.description.includes('one per line')) ||
        property.maxLength && property.maxLength > 255) {
      return 'textarea';
    }
    return 'text';
  }

  // Handle other types
  if (property.type === 'integer' || property.type === 'number') {
    return 'text'; // Will add number validation
  }

  // Default to text for unknown types
  return 'text';
}

/**
 * Generates user-friendly labels from field names
 */
function generateFieldLabel(fieldName: string, property: OpenAPIProperty): string {
  // Common field name mappings
  const labelMap: Record<string, string> = {
    // Certificate fields
    commonName: 'Common Name',
    subjectAlternativeNames: 'Subject Alternative Names',
    keyUsage: 'Key Usage',
    extendedKeyUsage: 'Extended Key Usage',
    certificateType: 'Certificate Type',
    validityPeriod: 'Validity Period',
    expiryDate: 'Expiry Date',
    issuedAt: 'Issued Date',
    expiresAt: 'Expires Date',
    createdAt: 'Created Date',
    updatedAt: 'Updated Date',
    serialNumber: 'Serial Number',
    organizationName: 'Organization Name',
    organizationalUnit: 'Organizational Unit',
    
    // Organization fields
    name: 'Name',
    email: 'Email Address',
    contactNumber: 'Phone Number',
    notificationEmail: 'Notification Email',
    notificationContactName: 'Notification Contact Name',
    
    // Product fields
    description: 'Description',
    environmentTypeId: 'Environment Type',
    environmentName: 'Environment Name',
    apiGatewayId: 'API Gateway ID',
    trustStoreId: 'Trust Store ID',
    s3BucketArn: 'S3 Bucket ARN',
    certificateId: 'Certificate',
    environmentId: 'Environment',
    
    // Common fields
    createdBy: 'Created By',
    updatedBy: 'Updated By',
    revokedBy: 'Revoked By',
    reason: 'Reason',
    status: 'Status',
    environment: 'Environment',
    country: 'Country',
    state: 'State/Province',
    locality: 'City/Locality'
  };

  if (labelMap[fieldName]) {
    return labelMap[fieldName];
  }

  // Generate from field name as fallback
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/\s+/g, ' ') // Clean up multiple spaces
    .trim();
}

/**
 * Generates select options from enum values
 */
function generateSelectOptions(enumValues: string[], fieldName: string): Array<{ value: string; label: string }> {
  // Domain-specific option mappings
  const optionMaps: Record<string, Record<string, string>> = {
    status: {
      'active': 'Active',
      'inactive': 'Inactive',
      'pending': 'Pending',
      'expired': 'Expired'
    },
    certificateType: {
      'server': 'Server Certificate',
      'client': 'Client Certificate',
      'ca': 'Certificate Authority'
    },
    keyUsage: {
      'digitalSignature': 'Digital Signature',
      'keyEncipherment': 'Key Encipherment',
      'dataEncipherment': 'Data Encipherment',
      'keyAgreement': 'Key Agreement',
      'keyCertSign': 'Key Certificate Sign',
      'cRLSign': 'CRL Sign'
    }
  };

  const fieldMap = optionMaps[fieldName] || {};

  return enumValues.map(value => ({
    value,
    label: fieldMap[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/([A-Z])/g, ' $1')
  }));
}

/**
 * Generates placeholder text from OpenAPI property
 */
function generatePlaceholder(property: OpenAPIProperty, fieldName: string): string | undefined {
  // Use example from OpenAPI spec
  if (property.example) {
    return `e.g., ${property.example}`;
  }

  // Extract examples from description
  const description = property.description || '';
  const exampleMatch = description.match(/e\.g\.?\s+([^,\)]+)/i);
  if (exampleMatch) {
    return `e.g., ${exampleMatch[1].trim()}`;
  }

  // Field-specific placeholders
  const placeholderMap: Record<string, string> = {
    email: 'e.g., user@example.com',
    notificationEmail: 'e.g., notifications@example.com',
    contactNumber: 'e.g., +44 20 1234 5678',
    commonName: 'e.g., api.example.com',
    name: 'e.g., My Organization',
    description: 'Enter a description...',
    environmentName: 'e.g., production, staging, development',
    organizationName: 'e.g., My Company Ltd',
    organizationalUnit: 'e.g., IT Department',
    country: 'e.g., GB',
    state: 'e.g., England',
    locality: 'e.g., London',
    subjectAlternativeNames: 'Enter one domain per line, e.g.:\napi.example.com\nwww.example.com',
    reason: 'e.g., Key compromised, Certificate superseded'
  };

  return placeholderMap[fieldName];
}

/**
 * Determines if a field should be hidden
 */
function shouldHideField(fieldName: string): boolean {
  const hiddenFields = [
    'id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
    'version', 'lastModified', 'internalId'
  ];
  return hiddenFields.includes(fieldName);
}

/**
 * Generates default values for fields
 */
function generateDefaultValue(property: OpenAPIProperty, fieldName: string): any {
  // Handle enum defaults
  if (property.enum) {
    const defaultMap: Record<string, string> = {
      status: 'active',
      certificateType: 'client', // Default to client certificates
      environment: 'non-prod', // Default to non-production
      country: 'GB' // Default country
    };
    return defaultMap[fieldName];
  }

  // Handle specific field defaults
  const fieldDefaults: Record<string, any> = {
    country: 'GB',
    keyUsage: ['digitalSignature', 'keyEncipherment'] // Default key usage for certificates
  };

  return fieldDefaults[fieldName];
}

/**
 * Universal function to parse any OpenAPI schema into form field configurations
 */
export function parseOpenAPISchemaToFields(
  openApiSpec: any,
  schemaName: string,
  hiddenFields: string[] = [],
  customLabels: Record<string, string> = {},
  customPlaceholders: Record<string, string> = {},
  customPatterns: Record<string, string> = {},
  customDefaults: Record<string, any> = {}
): FieldConfig[] {
  try {
    if (!openApiSpec || !openApiSpec.components || !openApiSpec.components.schemas) {
      console.error('Invalid OpenAPI specification provided');
      return [];
    }

    const schemas = openApiSpec.components.schemas;
    const schema = schemas[schemaName] as OpenAPISchema;
    
    if (!schema || !schema.properties) {
      console.error(`Schema ${schemaName} not found or invalid in OpenAPI spec`);
      console.log('Available schemas:', Object.keys(schemas));
      return [];
    }

    const requiredFields = schema.required || [];
    const fieldConfigs: FieldConfig[] = [];

    // Process each property in the schema
    Object.entries(schema.properties).forEach(([fieldName, property]) => {
      // Skip fields that should be hidden
      if (hiddenFields.includes(fieldName) || shouldHideField(fieldName)) {
        return;
      }

      const fieldType = mapOpenAPITypeToFieldType(property, fieldName);
      const isRequired = requiredFields.includes(fieldName);

      const config: FieldConfig = {
        name: fieldName,
        label: customLabels[fieldName] || generateFieldLabel(fieldName, property),
        type: fieldType,
        required: isRequired,
        helpText: property.description,
        placeholder: customPlaceholders[fieldName] || generatePlaceholder(property, fieldName),
        defaultValue: customDefaults[fieldName] !== undefined ? customDefaults[fieldName] : generateDefaultValue(property, fieldName)
      };

      // Add comprehensive validation properties
      // Pattern validation (custom patterns take precedence)
      if (customPatterns[fieldName]) {
        config.pattern = customPatterns[fieldName];
      } else if (property.pattern) {
        config.pattern = property.pattern;
      }

      // String length validation
      if (property.minLength !== undefined) {
        config.minLength = property.minLength;
      }
      if (property.maxLength !== undefined) {
        config.maxLength = property.maxLength;
      }

      // Number validation
      if (property.minimum !== undefined) {
        config.min = property.minimum;
      }
      if (property.maximum !== undefined) {
        config.max = property.maximum;
      }

      // Add smart validation defaults for common fields if not specified in schema
      if (!config.minLength && !config.maxLength) {
        const lengthDefaults: Record<string, { min?: number; max?: number }> = {
          commonName: { min: 1, max: 64 },
          organizationName: { min: 1, max: 64 },
          organizationalUnit: { min: 1, max: 64 },
          country: { min: 2, max: 2 },
          state: { min: 1, max: 128 },
          locality: { min: 1, max: 128 },
          email: { min: 5, max: 254 },
          notificationEmail: { min: 5, max: 254 },
          contactNumber: { min: 7, max: 20 },
          reason: { min: 10, max: 500 },
          name: { min: 1, max: 100 },
          description: { min: 10, max: 1000 }
        };

        const defaults = lengthDefaults[fieldName];
        if (defaults) {
          if (defaults.min) config.minLength = defaults.min;
          if (defaults.max) config.maxLength = defaults.max;
        }
      }

      // Add enum options for select fields
      if (property.enum && fieldType === 'select') {
        config.options = generateSelectOptions(property.enum, fieldName);
      }

      // Handle array enums (multi-select)
      if (property.type === 'array' && property.items?.enum && fieldType === 'select') {
        config.options = generateSelectOptions(property.items.enum, fieldName);
        config.multiple = true;
      }

      // Add textarea rows for multi-line fields
      if (fieldType === 'textarea') {
        config.rows = fieldName === 'description' ? 4 : 3;
      }

      fieldConfigs.push(config);
    });

    // Sort fields for better UX (required fields first, then optional)
    return fieldConfigs.sort((a, b) => {
      // Required fields before optional
      if (a.required && !b.required) return -1;
      if (b.required && !a.required) return 1;
      
      // Maintain original order otherwise
      return 0;
    });

  } catch (error) {
    console.error('Error parsing OpenAPI schema:', error);
    return [];
  }
}

/**
 * Gets available schemas from any OpenAPI spec
 */
export function getAvailableSchemas(openApiSpec: any): string[] {
  try {
    if (!openApiSpec || !openApiSpec.components || !openApiSpec.components.schemas) {
      return [];
    }
    return Object.keys(openApiSpec.components.schemas);
  } catch (error) {
    console.error('Error getting available schemas:', error);
    return [];
  }
}

/**
 * Validates that a schema exists in the OpenAPI spec
 */
export function validateSchemaExists(openApiSpec: any, schemaName: string): boolean {
  const availableSchemas = getAvailableSchemas(openApiSpec);
  return availableSchemas.includes(schemaName);
}